import React, { useEffect, useState } from 'react';
import { Route, Redirect, useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFlashMessage } from './FlashMessage';
import { dashboardService } from '../services/dashboardService';

/**
 * ProtectedRoute component for restricting access based on user permissions
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.requiresFullAccess - Whether this route requires full system access
 * @param {boolean} props.requiresCRMAccess - Whether this route requires CRM access (full or CRM-only)
 * @param {boolean} props.requiresFinancialAccess - Whether this route requires Financial access (full or financial-only)
 * @param {boolean} props.requiresCargoAccess - Whether this route requires Cargo access (full or cargo-only)
 * @param {boolean} props.requiresBunkersAccess - Whether this route requires Bunkers access (full or bunkers-only)
 * @param {boolean} props.requiresLegalAccess - Whether this route is the Legal module. CLOSED to everyone but superusers/staff (2026-08-26); contracts are read in context on the Enquiry, Delivery and CRM screens instead. Legal Approvers enter for the contracts sent to Legal (2026-10-17).
 * @param {boolean} props.requiresTradeOpsAccess - Whether this route is a shared Trade Ops surface (recaps/deliveries) — full OR cargo OR bunkers
 * @param {boolean} props.requiresFinancialOrTradeOpsAccess - Whether this route is a single invoice DOCUMENT (create/edit/detail) — full OR financial OR cargo OR bunkers. The operator who ran the delivery raises its invoice; the Financials lists, dashboard and master data stay on requiresFinancialAccess.
 * @param {boolean} props.requiresBunkerInvoiceApprovals - Whether this route is the Bunker Invoice Approvals screen — the server's `can_view_bunker_invoice_approvals` answer (Bunker Operations Approvers, plus superusers read-only). Never re-derived here.
 * @param {boolean} props.requiresTemplateAccess - Whether this route is the Template Headquarters — full OR financial OR designated template approver (matches backend HasTemplateHubAccess)
 * @param {boolean} props.requiresReportingAccess - Whether this route is Reporting Central — the server's `can_open_reporting_central` answer. Never re-derived here (the answer already covers superusers), so this gate cannot drift from backend HasReportingAccess.
 * @param {string} props.redirectTo - Where to redirect if access is denied (defaults to /dashboard)
 * @param {React.Component} props.component - The component to render if access is granted
 */
const ProtectedRoute = ({
  requiresFullAccess = false,
  requiresCRMAccess = false,
  requiresFinancialAccess = false,
  requiresCargoAccess = false,
  requiresBunkersAccess = false,
  requiresLegalAccess = false,
  requiresTradeOpsAccess = false,
  requiresFinancialOrTradeOpsAccess = false,
  requiresTemplateAccess = false,
  requiresReportingAccess = false,
  requiresBunkerInvoiceApprovals = false,
  redirectTo = '/dashboard',
  component: Component,
  render,
  ...rest
}) => {
  const { user, updateUser } = useAuth();
  const { showError } = useFlashMessage();
  const history = useHistory();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Always fetch user info to check authentication first
        const userInfo = await dashboardService.getUserInfo();
        
        // Update the auth context with fresh data
        updateUser(userInfo);
        
////        console.log('ProtectedRoute - User info:', userInfo);
        
        // Check if user has full access (including superuser and staff)
        const hasFullAccess = userInfo.has_full_access || userInfo.is_superuser || userInfo.is_staff;
        
        // Check if user has CRM access (full access OR CRM-only OR financial access)
        // Financial users need CRM access since invoicing depends on CRM data
        const hasCRMAccess = hasFullAccess || userInfo.has_crm_access || userInfo.has_financial_access || userInfo.is_financial_approver;
        
        // Check if user has Financial access (full access OR financial-only access OR financial approver)
        const hasFinancialAccess = hasFullAccess || userInfo.has_financial_access || userInfo.is_financial_approver;

        // Module-level trade gates (full access satisfies all of them).
        const hasCargoAccess = hasFullAccess || userInfo.has_cargo_access;
        const hasBunkersAccess = hasFullAccess || userInfo.has_bunkers_access;
        // Shared Trade Ops surfaces (recaps/deliveries): any trade-ops grant opens them.
        const hasTradeOpsAccess = hasFullAccess || userInfo.has_cargo_access || userInfo.has_bunkers_access;
        // Legal module is MANAGEMENT-ONLY (Craig, 2026-08-27: "managers keep
        // full list"). Closed to the desk — bunkers operators and customer
        // traders read contracts in context on the enquiry / delivery / freight
        // recap — while full-access managers keep the one screen that lists
        // every contract. Restores the pre-2026-07-16 population: Legal was
        // full-access-only before bunkers operators were let in.
        //
        // Must stay in step with backend CanAccessLegal. Granting more here
        // than the backend does sends the user to a screen that 403s on load;
        // granting less silently hides a screen they are entitled to, which is
        // exactly what happened when this line was first edited.
        // Legal Approvers too (2026-10-17): the server shows them the
        // contracts sent to Legal for review, which is where their
        // notification links.
        const hasLegalAccess = Boolean(hasFullAccess || userInfo.is_legal_approver);

        // Template Headquarters: full access OR financial access OR ANY
        // template-relevant approver role (so a domain approver notified of a
        // change can open the page to action it). Mirrors backend
        // HasTemplateHubAccess.
        const hasTemplateAccess = hasFullAccess
            || userInfo.has_financial_access
            || userInfo.can_approve_templates
            || userInfo.is_legal_approver
            || userInfo.is_financial_approver
            || userInfo.is_kyc_approver;

        // Determine if user has the required access level
        let accessGranted = true; // Default to true for unrestricted routes
        let errorMessage = "You don't have permission to access this resource. Please contact your administrator.";

        if (requiresFullAccess) {
          accessGranted = hasFullAccess;
          errorMessage = "You don't have permission to access this resource. Please contact your administrator for full system access.";
        } else if (requiresCRMAccess) {
          accessGranted = hasCRMAccess;
          errorMessage = "You don't have permission to access CRM. Please contact your administrator for CRM access.";
        } else if (requiresFinancialAccess) {
          accessGranted = hasFinancialAccess;
          errorMessage = "You don't have permission to access Financials. Please contact your administrator for Financial access.";
        } else if (requiresCargoAccess) {
          accessGranted = hasCargoAccess;
          errorMessage = "You don't have permission to access Trade Ops Cargo. Please contact your administrator for Cargo access.";
        } else if (requiresBunkersAccess) {
          accessGranted = hasBunkersAccess;
          errorMessage = "You don't have permission to access Trade Ops Bunkers. Please contact your administrator for Bunkers access.";
        } else if (requiresLegalAccess) {
          accessGranted = hasLegalAccess;
          errorMessage = "The Legal module has been retired. Contracts are on the enquiry, the delivery and the partner's CRM record.";
        } else if (requiresTradeOpsAccess) {
          accessGranted = hasTradeOpsAccess; // full OR cargo OR bunkers — any trade-ops grant
          errorMessage = "You don't have permission to access Trade Operations. Please contact your administrator for Cargo or Bunkers access.";
        } else if (requiresFinancialOrTradeOpsAccess) {
          // Single invoice documents only. A bunkers/cargo operator has to be
          // able to RAISE the invoice for a delivery they ran without being
          // handed the Financials module — the backend admits them through
          // invoicing.delivery_scoped_access and scopes the row to their
          // deliveries. Approval is still refused server-side, and the buttons
          // for it are hidden by hasFinancialAccess() on the screen itself.
          // Bunker Operations Approvers (2026-09-17) open every bunker delivery's
          // invoices from their approvals screen even with no Trade Ops module;
          // the backend admits them through the same delivery-scoped tier.
          accessGranted = hasFinancialAccess || hasTradeOpsAccess || Boolean(userInfo.can_view_bunker_invoice_approvals);
          errorMessage = "You don't have permission to open this invoice. Please contact your administrator for Financial access.";
        } else if (requiresBunkerInvoiceApprovals) {
          accessGranted = Boolean(userInfo.can_view_bunker_invoice_approvals);
          errorMessage = "The Bunker Invoice Approvals screen is for the Bunker Operations Approvals group. Ask an administrator for the Bunker Operations Approver role.";
        } else if (requiresTemplateAccess) {
          accessGranted = hasTemplateAccess;
          errorMessage = "You don't have permission to access the Template Headquarters. Please contact your administrator.";
        } else if (requiresReportingAccess) {
          // The server's own answer, taken as given. It already accounts for
          // superusers, so re-deriving it here from other flags is exactly how
          // a gate drifts from the backend and hands someone a screen that
          // 403s on load.
          accessGranted = userInfo.can_open_reporting_central === true;
          errorMessage = "You don't have permission to open Reporting Central. Please contact your administrator.";
        }
        // If no special access required, just being authenticated is enough (accessGranted stays true)
        
        if (accessGranted) {
          setHasAccess(true);
          setAccessDenied(false);
        } else {
          setHasAccess(false);
          setAccessDenied(true);
          showError(errorMessage, 7000);
          setTimeout(() => {
            history.push(redirectTo);
          }, 100);
        }
      } catch (error) {
        console.error('Error checking user access:', error);
        setHasAccess(false);
        setAccessDenied(true);
        showError("Unable to verify access permissions. Please try logging in again.", 7000);
        setTimeout(() => {
          history.push(redirectTo);
        }, 100);
      } finally {
        setIsChecking(false);
      }
    };

    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresFullAccess, requiresCRMAccess, requiresFinancialAccess, requiresCargoAccess, requiresBunkersAccess, requiresLegalAccess, requiresTradeOpsAccess, requiresFinancialOrTradeOpsAccess, requiresTemplateAccess, requiresReportingAccess]);

  // Show loading state while checking permissions
  if (isChecking) {
    return null;
  }

  // If access denied, redirect
  if (accessDenied && (requiresFullAccess || requiresCRMAccess || requiresFinancialAccess || requiresCargoAccess || requiresBunkersAccess || requiresLegalAccess || requiresTradeOpsAccess || requiresFinancialOrTradeOpsAccess || requiresTemplateAccess || requiresReportingAccess)) {
    return <Redirect to={redirectTo} />;
  }

  // Render the route if access is granted
  return (
    <Route
      {...rest}
      render={(props) => {
        if (!hasAccess) {
          return <Redirect to={redirectTo} />;
        }
        
        // If a render prop was provided, use it; otherwise use the component prop
        if (render) {
          return render(props);
        }
        
        return <Component {...props} />;
      }}
    />
  );
};

export default ProtectedRoute;

